<script setup lang="ts">
import { computed, provide, Ref, ref, watch, watchEffect } from "vue";
import {
	dataTableContext,
	DataTableFormatter,
	DataTableMetric,
} from "@/modules/explorer/components/Metrics/MetricResponseProcessor";
import MetricsDataTableRow from "@/modules/explorer/components/Metrics/Table/MetricsDataTableRow.vue";
import SingleMetricValueContextMenu from "@/modules/explorer/components/Metrics/SingleMetricValueContextMenu.vue";
import { useExplorerStore } from "@/modules/explorer/store";
import SingleMetricType = App.Data.Explorer.SingleMetric.SingleMetricType;
import DataTableResponseDTO = App.Data.Explorer.DataTable.DataTableResponseDTO;
import MetricsDataTableHeader from "@/modules/explorer/components/Metrics/Table/MetricsDataTableHeader.vue";
import SingleMetricPickDTO = App.Data.Explorer.SingleMetric.SingleMetricPickDTO;
import SingleMetricSumDTO = App.Data.Explorer.SingleMetric.SingleMetricSumDTO;
import DataTableMetricValueType = App.Enums.DataTableMetricValueType;
import SortDirection = App.Enums.SortDirection;
import TableAlertPropertiesDTO = App.Data.Alerts.TableAlertPropertiesDTO;
import router from "@/router/router";

const { dataTable, dataTableIndex } = defineProps<{
	dataTable: DataTableResponseDTO;
	dataTableIndex: number;
}>();

const emit = defineEmits<{
	(e: "setColumnAlert", type: TableAlertPropertiesDTO): void;
}>();

const sortBy = defineModel<string | null>("sortBy");
const sortDirection = defineModel<SortDirection | null>("sortDirection");

const isContextMenuVisible = ref(false);
const isColumnSelect = ref(false);
const contextMenuClickedEvent = ref<MouseEvent | null>(null);
const tmpSingleMetricConfiguration: Ref<SingleMetricPickDTO> = ref();

const openContextMenu = (
	event: MouseEvent,
	metric: DataTableMetric,
	valueIdx: number,
	valueColumn: DataTableMetricValueType,
	forColumn: boolean = false
) => {
	tmpSingleMetricConfiguration.value = {
		type: "pick",
		table_index: dataTableIndex,
		metric_name: metric.name,
		horizontal_dimension_value: metric.row.horizontal,
		offset_dimension_value: metric.subRow.offset,
		comparison_value: metric.values[valueIdx].comparison,
		value_column: valueColumn,
	};

	contextMenuClickedEvent.value = event;
	isContextMenuVisible.value = true;
	isColumnSelect.value = forColumn;
};

provide(dataTableContext, {
	openContextMenu,
	dataTableIndex,
});

const dataTableFormatter = computed(() => new DataTableFormatter(dataTable));

const headers = computed(() => dataTableFormatter.value.toHeaders());

const sortByInternal = computed({
	get() {
		if (!sortBy.value || !sortDirection.value) {
			return [];
		}

		return [{ key: sortBy.value as string, order: sortDirection.value }];
	},
	set(value) {
		const firstSortItem = value[0] || null;

		sortBy.value = firstSortItem?.key;
		sortDirection.value = firstSortItem?.order;
	},
});

const items = ref([]);
watchEffect(() => {
	items.value = dataTable.rows.flatMap((row) =>
		row.subRows.map((subRow, subRowIdx) => ({ row, subRowIdx, subRow }))
	);
});

const collapseHorizontal = computed(() => sortByInternal.value.length === 0);

const useSingleMetric = () => {
	useExplorerStore().document.single_metric =
		tmpSingleMetricConfiguration.value;
	tmpSingleMetricConfiguration.value = undefined;

	router.push({ name: "explorer.singleMetricWrapper" });
};

const getTableAlertPropertiesDTO = (): TableAlertPropertiesDTO => {
	return {
		table_index: tmpSingleMetricConfiguration.value.table_index,
		metric_name: tmpSingleMetricConfiguration.value.metric_name,
		comparison_value: tmpSingleMetricConfiguration.value.comparison_value,
		value_column: tmpSingleMetricConfiguration.value.value_column,
	};
};

const useColumnMetric = (type: SingleMetricType) => {
	let singleMetric: SingleMetricSumDTO = {
		...{
			type,
		},
		...getTableAlertPropertiesDTO(),
	};

	useExplorerStore().document.single_metric = singleMetric;
	router.push({ name: "explorer.singleMetricWrapper" });
};

const setColumnAlert = () => {
	emit("setColumnAlert", getTableAlertPropertiesDTO());
};
</script>

<template>
	<div class="metrics-data-table">
		<SingleMetricValueContextMenu
			v-model="isContextMenuVisible"
			:position="
				contextMenuClickedEvent
					? {
							x: contextMenuClickedEvent.clientX,
							y: contextMenuClickedEvent.clientY,
					  }
					: undefined
			"
			:is-column-select="isColumnSelect"
			@useSingleMetric="useSingleMetric"
			@useColumnMetric="useColumnMetric"
			@setColumnAlert="setColumnAlert"
		/>

		<v-data-table
			v-if="headers.length > 0"
			v-model:sort-by="sortByInternal"
			:headers="headers"
			:items="items"
			:items-per-page="10000"
			density="compact"
			class="dataTable"
		>
			<template v-slot:headers="{ headers, isSorted, getSortIcon, toggleSort }">
				<tr v-for="(row, y) in headers" :key="y">
					<metrics-data-table-header
						v-for="(column, x) in row"
						:key="x"
						:data-table="dataTable"
						:column="column"
						:is-sorted="isSorted"
						:get-sort-icon="getSortIcon"
						:toggle-sort="toggleSort"
					/>
				</tr>
			</template>
			<template v-slot:item="{ item }">
				<metrics-data-table-row
					:item="item"
					:data-table="dataTable"
					:collapse-horizontal="collapseHorizontal"
				/>
			</template>
			<template v-if="items.length < 10000" v-slot:bottom></template>
		</v-data-table>
	</div>
</template>

<style lang="scss">
.metrics-data-table {
	th.v-data-table__th--sortable.v-data-table-column--align-center > div {
		margin-right: -18px;
	}

	&__row {
		$self: &;

		&--is-first {
			border-top: 2px solid rgba(var(--v-border-color), 0.2) !important;
		}

		&--depth-0 {
			//background: rgba(var(--v-theme-primary), 0.05) !important;
		}

		&--depth-1 {
			&#{ $self }--collapse-horizontal td.metric-horizontal {
				padding-left: 32px !important;
			}
		}
	}

	&__metric {
		white-space: nowrap;

		&--single-metric {
			text-decoration: underline dotted;
			font-weight: bold;
		}
	}

	&--color-1 {
		background-color: rgba(var(--color-background), 0.6);
	}
	&--color-2 {
		background-color: #e6e6fa;
	} // Soft Lavender
	&--color-3 {
		background-color: #f5fffa;
	} // Pale Mint
	&--color-4 {
		background-color: #ffe5b4;
	} // Creamy Peach
	&--color-5 {
		background-color: #fffacd;
	} // Pale Yellow
	&--color-6 {
		background-color: #f08080;
	} // Light Coral
	&--color-7 {
		background-color: #b0e0e6;
	} // Powder Blue
	&--color-8 {
		background-color: #778899;
	} // Light Slate Gray
	&--color-9 {
		background-color: #fff0f5;
	} // Lavender Blush
	&--color-10 {
		background-color: #f0fff0;
	} // Honeydew
	&--color-11 {
		background-color: #cfe8d5;
	}

	.dataTable {
		font-size: 0.75rem !important;
		font-weight: 400;
		line-height: 1.667;
		letter-spacing: 0.0333333333em !important;
		text-transform: none !important;
	}

	.dataTable td {
		border-bottom: thin solid rgba(var(--v-border-color), 0.2);
	}

	table {
		border-collapse: collapse !important;
	}

	.thick-border {
		border-bottom: 2px solid rgba(var(--v-border-color), 0.2) !important;
	}

	&__metric {
		text-align: right;
	}

	.v-table .v-table__wrapper > table > tbody > tr:not(:last-child) > th,
	.v-table .v-table__wrapper > table > thead > tr:last-child > th {
		border-bottom: 2px solid rgba(var(--v-border-color), 0.2) !important;
	}
}
</style>
